import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import DiscountBanner from '../components/DiscountBanner';
import ReviewBanner from '../components/ReviewBanner';

const GalleryPage = () => {
  const galleryImages = [
    {
      id: 'gallery-1',
      url: "https://customer-assets.emergentagent.com/job_eb1246c6-a55c-45ff-ad7f-691bbbc2d9d7/artifacts/co9ugeyl_15758.jpg",
      alt: "A happy client cat we've cared for"
    },
    {
      id: 'gallery-2',
      url: "https://customer-assets.emergentagent.com/job_eb1246c6-a55c-45ff-ad7f-691bbbc2d9d7/artifacts/r5vmqxra_15771.jpg",
      alt: "Cosy nap time during a visit"
    },
    {
      id: 'gallery-3',
      url: "https://customer-assets.emergentagent.com/job_eb1246c6-a55c-45ff-ad7f-691bbbc2d9d7/artifacts/eaqyv7fm_15773.jpg",
      alt: "Relaxed kitty enjoying home comforts"
    },
    {
      id: 'gallery-4',
      url: "https://customer-assets.emergentagent.com/job_eb1246c6-a55c-45ff-ad7f-691bbbc2d9d7/artifacts/tu74y881_15776.jpg",
      alt: "Playtime with one of our regulars"
    },
    {
      id: 'gallery-5',
      url: "https://customer-assets.emergentagent.com/job_eb1246c6-a55c-45ff-ad7f-691bbbc2d9d7/artifacts/njxist10_15804.jpg",
      alt: "A purr-fect moment captured on a visit"
    },
    {
      id: 'gallery-6',
      url: "https://customer-assets.emergentagent.com/job_5b1418a9-0c24-4109-b691-12676e679be7/artifacts/j9pgzxl6_15767.jpg",
      alt: "Belly rubs and chin scratches on a visit"
    },
    {
      id: 'gallery-7',
      url: "https://customer-assets.emergentagent.com/job_5b1418a9-0c24-4109-b691-12676e679be7/artifacts/pxc5nuar_15769.jpg",
      alt: "Handsome ginger client posing for the camera"
    },
    {
      id: 'gallery-8',
      url: "https://customer-assets.emergentagent.com/job_5b1418a9-0c24-4109-b691-12676e679be7/artifacts/1q91vw8x_15781.jpg",
      alt: "Tuxedo cat strutting down the hallway"
    },
    {
      id: 'gallery-9',
      url: "https://customer-assets.emergentagent.com/job_5b1418a9-0c24-4109-b691-12676e679be7/artifacts/uhog4hfb_15794.jpg",
      alt: "Rob enjoying cuddles with a happy client"
    },
    {
      id: 'gallery-10',
      url: "https://customer-assets.emergentagent.com/job_5b1418a9-0c24-4109-b691-12676e679be7/artifacts/wwyv72i5_15801.jpg",
      alt: "Sleepy close-up of one of our regulars"
    },
    {
      id: 'gallery-11',
      url: "https://images.pexels.com/photos/18188816/pexels-photo-18188816.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      alt: "Grey cat playing with toy snake"
    },
    {
      id: 'gallery-12',
      url: "https://images.unsplash.com/photo-1762618530429-551903de34ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxjYXQlMjBzaXR0aW5nJTIwZnVsbCUyMGJvZHklMjBwb3J0cmFpdCUyMGluZG9vcnxlbnwwfHx8fDE3NzMxOTA4NTl8MA&ixlib=rb-4.1.0&q=85",
      alt: "Fluffy cat resting on armchair"
    }
  ];

  const [activeIndex, setActiveIndex] = useState(null);
  const isOpen = activeIndex !== null;

  const closeLightbox = useCallback(() => setActiveIndex(null), []);

  const showPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setActiveIndex((i) => (i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length));
  }, [galleryImages.length]);

  const showNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setActiveIndex((i) => (i === null ? null : (i + 1) % galleryImages.length));
  }, [galleryImages.length]);

  // Keyboard navigation + body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, closeLightbox, showPrev, showNext]);

  const activeImage = isOpen ? galleryImages[activeIndex] : null;

  return (
    <div className="gallery-page">
      {/* Discount Banner - Top */}
      <DiscountBanner size="large" />
      
      {/* Review Banner */}
      <ReviewBanner />
      
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Photo Gallery</h1>
          <p className="page-subtitle">Happy cats we've cared for in their homes</p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="gallery-section">
        <div className="container">
          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <button
                key={image.id}
                type="button"
                className="gallery-item gallery-item-button"
                onClick={() => setActiveIndex(index)}
                aria-label={`Enlarge image: ${image.alt}`}
                data-testid={`gallery-item-${image.id}`}
              >
                <img 
                  src={image.url} 
                  alt={image.alt}
                  className="gallery-image"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery CTA */}
      <section className="gallery-cta-section">
        <div className="container">
          <div className="gallery-cta-box">
            <h2>Could Your Cat Be Our Next Superstar?</h2>
            <p>Let us care for your feline friends with the same love and attention shown to all our clients.</p>
            <p className="gallery-note">Note: Featured photos include real client cats we've cared for, alongside professional images representing the quality of care and comfortable home environments we provide.</p>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {isOpen && activeImage && (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged gallery image"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            aria-label="Close enlarged image"
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>

          <button
            type="button"
            className="lightbox-nav lightbox-prev"
            onClick={showPrev}
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>

          <figure
            className="lightbox-figure"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeImage.url}
              alt={activeImage.alt}
              className="lightbox-image"
              data-testid="lightbox-image"
            />
            <figcaption className="lightbox-caption">
              {activeImage.alt}
              <span className="lightbox-counter">{activeIndex + 1} / {galleryImages.length}</span>
            </figcaption>
          </figure>

          <button
            type="button"
            className="lightbox-nav lightbox-next"
            onClick={showNext}
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
